import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import AuthService from "services/auth.service";
import { FaBtc, FaArrowLeft, FaUpload, FaCheck, FaTimes, FaMoneyBillWave, FaCopy } from "react-icons/fa";
import Btc from "../assets/img/digital/btc.svg";
import Eth from "../assets/img/digital/eth.svg";
import Usdt from "../assets/img/digital/usdt.svg";
import Usdc from "../assets/img/digital/usdc.svg";
import apple from "../assets/img/digital/apple.svg";
import ebay from "../assets/img/digital/ebay.svg";
import Sephora from "../assets/img/digital/sephora.svg";
import walmart from "../assets/img/digital/walmart.svg";
import cashapp from "../assets/img/digital/cashapp.svg";
import paypal from "../assets/img/digital/paypal.svg"; 
import zelle from "../assets/img/digital/zelle.svg";
import chime from "../assets/img/digital/chime.svg";
import venmo from "../assets/img/digital/venmo.svg";
import "./DepositModal.css";

const DepositModal = ({ isOpen, onClose }) => {
  const [wallets, setWallets] = useState([]);
  const [p2pWallets, setP2pWallets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState("list"); // "list", "crypto", "p2p", or "upload"
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [successful, setSuccessful] = useState(false);

  // Map currency names to their respective icons (for both gift cards and wallets)
  const currencyIcons = {
    BTC: Btc,
    ETH: Eth,
    USDT: Usdt,
    USDC: Usdc,
    APPLEPAY: apple,
    EBAY: ebay,
    SEPHORA: Sephora,
    WALMART: walmart,
    CASHAPP: cashapp,
    PAYPAL: paypal,
    ZELLE: zelle,
    CHIME: chime,
    VENMO: venmo,
    OTHERS: null,
  };

  // Helper function to get icon URL from database icon field (for wallets)
  const getIconUrl = (icon) => {
    if (!icon) return null;
    
    // If icon is already a full URL, return it
    if (icon.startsWith('http://') || icon.startsWith('https://')) {
      return icon;
    }
    
    // Clean the icon name for matching
    const cleanIconName = icon.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    // Check if we have a matching imported icon
    for (const [key, importedIcon] of Object.entries(currencyIcons)) {
      if (importedIcon && key.toLowerCase() === cleanIconName) {
        return importedIcon;
      }
    }
    
    // If no exact match, try partial matching
    for (const [key, importedIcon] of Object.entries(currencyIcons)) {
      if (importedIcon && cleanIconName.includes(key.toLowerCase())) {
        return importedIcon;
      }
    }
    
    return null;
  };

  // Gift card options - all will use upload form
  const giftCardOptions = [
    { id: "apple", name: "Apple", type: "giftcard" },
    { id: "ebay", name: "eBay", type: "giftcard" },
    { id: "sephora", name: "Sephora", type: "giftcard" },
    { id: "walmart", name: "Walmart", type: "giftcard" },
    { id: "others", name: "Others", type: "giftcard" },
  ];

  
  useEffect(() => {
    if (!isOpen) return;
    
    // Load crypto wallets
    AuthService.getWallets()
      .then((res) => setWallets(res.data.filter(w => w.enabled !== false)))
      .catch(() => setWallets([]));
    
    // Load P2P wallets
    AuthService.getP2PWallets()
      .then((res) => setP2pWallets(res.data.filter(w => w.enabled !== false)))
      .catch(() => setP2pWallets([]));
      
  }, [isOpen]);

  // Reset view when modal closes
  useEffect(() => {
    if (!isOpen) {
      setView("list");
      setSelected(null);
      setFrontImage(null);
      setBackImage(null);
      setFrontFile(null);
      setBackFile(null);
      setUploading(false);
      setMessage("");
      setSuccessful(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    if (!selected) return;
    try {
      let textToCopy = selected.tagLabel;
      if (selected.tagLabel) {
        textToCopy = `${selected.tagLabel}`;
      }
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = selected.address;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSelectWallet = (wallet) => {
    setSelected(wallet);
    setView("crypto");
  };

  const handleSelectGiftCard = (giftcard) => {
    setSelected(giftcard);
    setView("upload");
  };

  const handleSelectP2P = (p2pWallet) => {
    setSelected(p2pWallet);
    setView("p2p");
  };

  // Helper function to get default tag label based on service name
  const getDefaultTagLabel = (serviceName) => {
    switch (serviceName.toLowerCase()) {
      case 'cashapp':
        return 'Cashtag';
      case 'paypal':
        return 'Email';
      case 'chime':
        return 'chimeSign';
      case 'zelle':
        return 'Email or Phone';
      default:
        return 'Username';
    }
  };

  const handleBack = () => {
    setView("list");
    setSelected(null);
    setFrontImage(null);
    setBackImage(null);
    setFrontFile(null);
    setBackFile(null);
    setMessage("");
    setSuccessful(false);
  };

  // Get the appropriate icon based on item type and icon field
  const getWalletIcon = (item) => {
    // For gift cards, use the hardcoded mapping
    if (item.type === "giftcard") {
      const upperIcon = item.name.toUpperCase();
      for (const [key, icon] of Object.entries(currencyIcons)) {
        if (upperIcon.includes(key)) {
          return icon;
        }
      }
      return null;
    }
    
    // For wallets (crypto/p2p), use the database icon field
    return getIconUrl(item.icon);
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage("Please select an image file");
        setSuccessful(false);
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage("File size must be less than 5MB");
        setSuccessful(false);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        if (type === "front") {
          setFrontImage(event.target.result);
          setFrontFile(file);
        } else {
          setBackImage(event.target.result);
          setBackFile(file);
        }
        setMessage(""); // Clear any previous messages
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitGiftCard = async () => {
    if (!frontFile || !backFile || !selected) return;

    setUploading(true);
    setMessage("");
    
    try {
      const currentUser = AuthService.getCurrentUser();

      if (!currentUser || !currentUser.id) {
        setMessage("User not authenticated. Please log in again.");
        setSuccessful(false);
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append("frontImage", frontFile);
      formData.append("backImage", backFile);
      formData.append("cardType", selected.name);
      formData.append("userId", currentUser.id);
      formData.append("userEmail", currentUser.email);
      formData.append("userName", `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim());

      const response = await AuthService.uploadCard(formData);
      setMessage("Gift card submitted successfully! Our team will review it shortly.");
      setSuccessful(true);
      
      // Reset form after successful upload
      setFrontImage(null);
      setBackImage(null);
      setFrontFile(null);
      setBackFile(null);
      
      setTimeout(() => {
        onClose();
      }, 3000);
      
    } catch (error) {
      const resMessage =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        "Error uploading gift card. Please try again.";
      setMessage(resMessage);
      setSuccessful(false);
    } finally {
      setUploading(false);
    }
  };

  const SelectedIcon = selected ? getWalletIcon(selected) : null;

  // Filter wallets by type for different sections
  const cryptoWallets = wallets.filter(w => w.type === "crypto" || !w.type);

  const renderPaymentGrid = (items, onClickHandler) => (
    <div className="payment-grid">
      {items.map((item) => {
        const iconUrl = getWalletIcon(item);
        console.log(iconUrl);
        return (
          <button
            key={item.id}
            className="payment-option-button"
            onClick={() => onClickHandler(item)}
          >
            {iconUrl ? (
              <img src={iconUrl} alt={item.name} width={24} height={24} className="payment-option-icon" />
            ) : (
              <div className="payment-option-placeholder" />
            )}
            <span className="payment-option-text">{item.name}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && onClose()}>
      <div className="modal-content deposit-modal">
        <button className="close-button" onClick={onClose}>
          ×
        </button>
        
        {view === "list" ? (
          <>
            <h3 className="modal-title">Add Money</h3>
            
            {/* P2P Payments - Now using backend data */}
            {p2pWallets.length > 0 && (
              <div className="payment-section">
                <h4 className="section-title">
                  <FaMoneyBillWave className="section-icon" />
                  P2P Payments
                </h4>
                {renderPaymentGrid(p2pWallets, handleSelectP2P)}
              </div>
            )}

            {/* Crypto Wallets */}
            {cryptoWallets.length > 0 && (
              <div className="payment-section">
                <h4 className="section-title">
                  <FaBtc className="section-icon" />
                  Cryptocurrency
                </h4>
                {renderPaymentGrid(cryptoWallets, handleSelectWallet)}
              </div>
            )}

            {/* Gift Cards */}
            {/* <div className="payment-section">
              <h4 className="section-title">🎁 Money Cards</h4>
              {renderPaymentGrid(giftCardOptions, handleSelectGiftCard)}
            </div> */}
          </>
        ) : view === "crypto" ? (
          // Crypto Deposit View
          <>
            <div className="modal-header">
              <button className="back-button" onClick={handleBack}>
                <FaArrowLeft />
              </button>
              <h3 className="modal-title">Deposit {selected?.name}</h3>
            </div>

            <div className="crypto-deposit-content">
              <div className="qr-code-container">
                <QRCode value={selected?.address || ""} size={164} />
              </div>
              <div className="address-info">
                <div className="info-group">
                  <div className="info-label">Wallet - Network</div>
                  <div className="info-value">{selected?.name}{selected?.network ? ` · ${selected.network}` : ""}</div>
                </div>
                <div className="info-group">
                  <div className="info-label">Address</div>
                  <div className="address-display">
                    {selected?.address}
                  </div>
                </div>
                <div className="action-buttons">
                  <button className="copy-button primary-btn" onClick={handleCopy}>
                    {copied ? <FaCheck /> : <FaCopy />}
                    {copied ? "Copied" : "Copy Address"}
                  </button>
                </div>
              </div>
            </div>

            <div className="instructions-box">
              <h5>💡 Important Instructions</h5>
              <ul>
                <li>Send only <strong>{selected?.name}</strong> to this address</li>
                <li>Ensure you're using the correct network: <strong>{selected?.network || 'Default'}</strong></li>
                <li>Transactions may take 10-30 minutes to confirm</li>
                <li>Contact support if you need assistance</li>
              </ul>
            </div>
          </>
        ) : view === "p2p" ? (
          // P2P Payment View
          <>
            <div className="modal-header">
              <button className="back-button" onClick={handleBack}>
                <FaArrowLeft />
              </button>
              <h3 className="modal-title">Send with {selected?.name}</h3>
            </div>

            <div className="p2p-content">
              {SelectedIcon && (
                <img 
                  src={SelectedIcon} 
                  alt={selected?.name} 
                  className="p2p-icon"
                />
              )}
              
              <div className="p2p-info-card">
                <h4>Send Payment To:</h4>
                
                <div className="p2p-address-group">
                  <div className="info-label">
                    {selected?.name} {selected?.requiresTag ? "tag" : "Tag"}
                  </div>
                  <div className="p2p-address-display">
                    {selected?.tagLabel}
                  </div>
                </div>

                

                <button 
                  className="copy-button primary-btn large" 
                  onClick={handleCopy}
                  disabled={!selected?.tagLabel}
                >
                  {copied ? <FaCheck /> : <FaCopy />}
                  {copied ? "Copied!" : `Copy ${selected?.name} Info`}
                </button>
              </div>

              

             
            </div>
          </>
        ) : (
          // Upload View for ALL gift cards
          <>
            <div className="modal-header">
              <button className="back-button" onClick={handleBack}>
                <FaArrowLeft />
              </button>
              <h3 className="modal-title">Upload {selected?.name} Gift Card</h3>
            </div>

            <div className="upload-content">
              {/* Message Display */}
              {message && (
                <div className={`message ${successful ? 'success' : 'error'}`}>
                  {successful ? <FaCheck /> : <FaTimes />}
                  {message}
                </div>
              )}

              <div className="upload-header">
                {SelectedIcon && (
                  <img 
                    src={SelectedIcon} 
                    alt={selected?.name} 
                    className="upload-icon"
                  />
                )}
                <h4>Upload Gift Card Images</h4>
                <p>
                  Please upload clear images of the front and back of your {selected?.name} gift card.
                  Make sure all details are visible and readable.
                </p>
              </div>

              <div className="upload-grid">
                <div className="upload-section">
                  <label>Front Side</label>
                  <div
                    className={`upload-area ${frontImage ? 'has-image' : ''}`}
                    style={{ backgroundImage: frontImage ? `url(${frontImage})` : 'none' }}
                    onClick={() => document.getElementById('front-upload').click()}
                  >
                    {frontImage ? (
                      <div className="upload-success">
                        <div>✓ Front Image Uploaded</div>
                        <div>Click to change</div>
                      </div>
                    ) : (
                      <>
                        <FaUpload className="upload-icon" />
                        <div>Click to upload front</div>
                        <div>PNG, JPG up to 5MB</div>
                      </>
                    )}
                    <input
                      id="front-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "front")}
                    />
                  </div>
                </div>

                <div className="upload-section">
                  <label>Back Side</label>
                  <div
                    className={`upload-area ${backImage ? 'has-image' : ''}`}
                    style={{ backgroundImage: backImage ? `url(${backImage})` : 'none' }}
                    onClick={() => document.getElementById('back-upload').click()}
                  >
                    {backImage ? (
                      <div className="upload-success">
                        <div>✓ Back Image Uploaded</div>
                        <div>Click to change</div>
                      </div>
                    ) : (
                      <>
                        <FaUpload className="upload-icon" />
                        <div>Click to upload back</div>
                        <div>PNG, JPG up to 5MB</div>
                      </>
                    )}
                    <input
                      id="back-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "back")}
                    />
                  </div>
                </div>
              </div>

              <button 
                className="submit-button primary-btn large" 
                onClick={handleSubmitGiftCard}
                disabled={!frontImage || !backImage || uploading}
              >
                {uploading ? (
                  <>⏳ Uploading...</>
                ) : (
                  <>📤 Submit {selected?.name} Gift Card</>
                )}
              </button>

              <div className="upload-note">
                After submission, our team will verify your gift card and credit your account.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DepositModal;